"use client"

import { HttpTypes } from "@medusajs/types"
import type { JSX } from "react"
import React from "react"

// SEO-optimized breadcrumb item component with mobile optimization
const ProductBreadcrumbItem = ({
  title,
  countryCode,
  handle,
  /** When set without handle, links to `/${countryCode}${path}` (e.g. `/store`). */
  path,
  isCurrentPage = false,
  isMobile = false,
}: {
  title: string
  countryCode: string
  handle?: string
  path?: string
  isCurrentPage?: boolean
  isMobile?: boolean
}) => {
  // Mobile truncation logic
  const getDisplayTitle = (title: string, isMobile: boolean) => {
    if (!isMobile) return title
    if (title.length <= 15) return title
    return title.substring(0, 12) + "..."
  }

  if (isCurrentPage) {
    return (
      <li 
        className="text-neutral-900 font-semibold text-sm md:text-base truncate max-w-[150px] sm:max-w-[200px] md:max-w-none" 
        key={handle}
        aria-current="page"
        title={title} // Full title on hover
      >
        {getDisplayTitle(title, isMobile)}
      </li>
    )
  }

  return (
    <li 
      className="text-neutral-500 hover:text-neutral-700 transition-colors duration-200" 
      key={handle}
    >
      <a
        className="hover:text-neutral-900 text-sm md:text-base font-medium truncate max-w-[120px] sm:max-w-[150px] md:max-w-none block"
        href={
          handle
            ? `/${countryCode}/categories/${handle}`
            : path
              ? `/${countryCode}${path.startsWith("/") ? path : `/${path}`}`
              : `/${countryCode}`
        }
        title={title} // Full title on hover
      >
        {getDisplayTitle(title, isMobile)}
      </a>
    </li>
  )
}

const ProductBreadcrumb = ({
  product,
  countryCode,
  allCategories,
}: {
  product: HttpTypes.StoreProduct
  countryCode: string
  allCategories: HttpTypes.StoreProductCategory[]
}) => {
  // Mobile detection with hydration-safe approach
  const [isMobile, setIsMobile] = React.useState(false)
  const [isClient, setIsClient] = React.useState(false)

  React.useEffect(() => {
    setIsClient(true)
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  const generateBreadcrumbs = (
    product: HttpTypes.StoreProduct
  ): JSX.Element[] => {
    const breadcrumbs: JSX.Element[] = []

    // Add product name as the final breadcrumb (current page)
    breadcrumbs.push(
      <ProductBreadcrumbItem
        title={product.title}
        countryCode={countryCode}
        key="product"
        isCurrentPage={true}
        isMobile={isClient && isMobile}
      />
    )

    // If product has categories, add the first category and its parents
    if (product.categories && product.categories.length > 0) {
      const primaryCategory = product.categories[0]
      
      // Add separator before category
      breadcrumbs.unshift(
        <li 
          className="text-neutral-400 mx-2 md:mx-3 text-xs md:text-sm" 
          key="separator-category"
          aria-hidden="true"
        >
          <span className="hidden sm:inline">›</span>
          <span className="sm:hidden">/</span>
        </li>
      )

      // Add the primary category
      breadcrumbs.unshift(
        <ProductBreadcrumbItem
          title={primaryCategory.name}
          countryCode={countryCode}
          handle={primaryCategory.handle}
          key={primaryCategory.id}
          isMobile={isClient && isMobile}
        />
      )

      // Add parent categories recursively
      let currentCategory = allCategories.find(
        (c) => c.id === primaryCategory.parent_category_id
      )

      while (currentCategory) {
        breadcrumbs.unshift(
          <li 
            className="text-neutral-400 mx-2 md:mx-3 text-xs md:text-sm" 
            key={`separator-${currentCategory.id}`}
            aria-hidden="true"
          >
            <span className="hidden sm:inline">›</span>
            <span className="sm:hidden">/</span>
          </li>
        )

        breadcrumbs.unshift(
          <ProductBreadcrumbItem
            title={currentCategory.name}
            countryCode={countryCode}
            handle={currentCategory.handle}
            key={currentCategory.id}
            isMobile={isClient && isMobile}
          />
        )

        currentCategory = allCategories.find(
          (c) => c.id === currentCategory?.parent_category_id
        )
      }
    }

    // Add base separator and "Izdelki" link
    breadcrumbs.unshift(
      <li 
        className="text-neutral-400 mx-2 md:mx-3 text-xs md:text-sm" 
        key="separator-base"
        aria-hidden="true"
      >
        <span className="hidden sm:inline">›</span>
        <span className="sm:hidden">/</span>
      </li>
    )

    breadcrumbs.unshift(
      <ProductBreadcrumbItem 
        title="Izdelki" 
        countryCode={countryCode}
        path="/store"
        key="base"
        isMobile={isClient && isMobile}
      />
    )

    return breadcrumbs
  }

  const breadcrumbs = generateBreadcrumbs(product)

  return (
    <>
      {/* Semantic breadcrumb navigation */}
      <nav 
        className="flex items-center" 
        aria-label="Breadcrumb navigation"
        role="navigation"
      >
        <ol 
          className="flex items-center flex-wrap gap-1 md:gap-2 text-sm md:text-base"
        >
          {breadcrumbs}
        </ol>
      </nav>
    </>
  )
}

export default ProductBreadcrumb